import React, { useEffect, useState } from "react";
import { Alert, Button, Collapse, Descriptions, Modal, Space, Table, Typography, message as antdMessage } from "antd";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { COMPANY_COUNTRY_CODE } from "../../../../../constants/CompanyConstants";
import { sendDesadv } from "../../../../../functions/edifact";

const { Text } = Typography;

/**
 * Laatste controle vóór het versturen van een al gegenereerde DESADV via
 * Descartes: toont alle gegevens uit edifactDesadvSummary (kop + per pallet)
 * en het ruwe bericht. De SSCC-kolom blijft leeg zolang RESERVE_SSCCS uit
 * staat in de API (zie desadv-constants.ts).
 */
const SendDesadvModal = ({ visible, order, onCancel, onSent }) => {
  const { t } = useTranslation();
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (visible) setError(null);
  }, [visible]);

  const summary = order?.edifactDesadvSummary;

  const alreadySent = !!order?.edifactDesadvSentAt;

  // Al verstuurd: eerst bevestigen, Descartes stuurt het anders gewoon nog een keer door.
  const confirmSend = () =>
    alreadySent
      ? Modal.confirm({
          title: t("send_desadv_again"),
          content: t("confirm_send_desadv_again"),
          okText: t("send_again"),
          cancelText: t("cancel"),
          onOk: handleSend,
        })
      : handleSend();

  const handleSend = async () => {
    setSending(true);
    setError(null);
    try {
      const { tid } = await sendDesadv(order.id, COMPANY_COUNTRY_CODE);
      antdMessage.success(t("desadv_sent_successfully", { tid: tid || "-" }));
      onSent();
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setSending(false);
    }
  };

  const columns = [
    { title: "#", key: "index", render: (_, __, index) => index + 1 },
    {
      title: "SSCC",
      dataIndex: "sscc",
      key: "sscc",
      render: (sscc) => sscc || <Text type="secondary">—</Text>,
    },
    { title: "GTIN", dataIndex: "gtin", key: "gtin" },
    { title: t("edifact_line_quantity"), dataIndex: "quantity", key: "quantity" },
    { title: t("desadv_batch_number"), dataIndex: "batchNumber", key: "batchNumber" },
    { title: "THT", dataIndex: "bestBeforeDate", key: "bestBeforeDate" },
    {
      title: "RTI krat",
      key: "crate",
      render: (_, unit) => `${unit.crateQuantity} × ${unit.crateGtin}`,
    },
    {
      title: "RTI pallet",
      key: "pallet",
      render: (_, unit) => `1 × ${unit.palletGtin}`,
    },
  ];

  const ssccMissing = summary?.units?.some((unit) => !unit.sscc);

  return (
    <Modal
      title={t("send_desadv_title")}
      open={visible}
      onCancel={onCancel}
      width={1100}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          {t("cancel")}
        </Button>,
        <Button key="send" type="primary" loading={sending} disabled={!summary} onClick={confirmSend}>
          {t(alreadySent ? "send_again" : "send")}
        </Button>,
      ]}
    >
      {summary && (
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <Text type="secondary">{t("send_desadv_message")}</Text>
          {order.edifactDesadvSentAt && (
            <Alert
              type="warning"
              showIcon
              message={t("send_desadv_already_sent", {
                date: dayjs.unix(order.edifactDesadvSentAt).format("DD-MM-YYYY HH:mm"),
                tid: order.edifactDesadvTid || "-",
              })}
            />
          )}
          <Descriptions bordered size="small" column={2}>
            <Descriptions.Item label="Despatch Advice Number">{summary.despatchAdviceNumber}</Descriptions.Item>
            <Descriptions.Item label="Order Number">{summary.orderNumber}</Descriptions.Item>
            <Descriptions.Item label="Message Date">
              {summary.messageDate} {summary.messageTime}
            </Descriptions.Item>
            <Descriptions.Item label="Estimated Delivery Date">
              {summary.estimatedDeliveryDate} {summary.estimatedDeliveryTime}
            </Descriptions.Item>
            <Descriptions.Item label="GLN Buyer">{summary.buyerGln}</Descriptions.Item>
            <Descriptions.Item label="GLN Delivery Party">{summary.deliveryPartyGln}</Descriptions.Item>
          </Descriptions>
          {ssccMissing && <Alert type="info" showIcon message={t("desadv_sscc_pending")} />}
          <Table
            rowKey={(_, index) => index}
            columns={columns}
            dataSource={summary.units || []}
            pagination={false}
            size="small"
          />
          <Collapse
            items={[
              {
                key: "raw",
                label: t("desadv_raw_message"),
                children: (
                  <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", margin: 0, fontSize: 12 }}>
                    {order.edifactDesadvMessage}
                  </pre>
                ),
              },
            ]}
          />
          {error && <Alert type="error" showIcon message={error} />}
        </Space>
      )}
    </Modal>
  );
};

export default SendDesadvModal;
